import React, { useState, useRef, useEffect } from 'react';
import { Empty, Button, Space, Select, Slider } from 'antd';
import { SwapOutlined, FullscreenOutlined } from '@ant-design/icons';
import './ImageComparer.css';

const ImageComparer = ({ 
  inputs, 
  outputs, 
  selectedInputs,
  selectedOutputs,
  onImageClick 
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const comparerRef = useRef(null);
  
  // Get currently selected images or default to first available
  const inputImages = selectedInputs?.value?.length > 0 ? selectedInputs.value : inputs;
  const outputImages = selectedOutputs?.value?.length > 0 ? selectedOutputs.value : outputs;
  
  const currentInput = inputImages[currentInputIndex];
  const currentOutput = outputImages[currentOutputIndex];
  
  useEffect(() => {
    // Reset indices when selections change
    setCurrentInputIndex(0);
    setCurrentOutputIndex(0);
  }, [selectedInputs?.value, selectedOutputs?.value]);
  
  useEffect(() => {
    const handleMouseMoveEvent = (e) => {
      if (!isDragging || !comparerRef.current) return;
      
      const rect = comparerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPosition(Math.min(100, Math.max(0, percentage)));
    };
    
    const handleMouseUpEvent = () => {
      setIsDragging(false);
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMoveEvent);
      document.addEventListener('mouseup', handleMouseUpEvent);
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveEvent);
        document.removeEventListener('mouseup', handleMouseUpEvent);
      };
    }
  }, [isDragging]);
  
  const handleSwap = () => {
    const tempIndex = currentInputIndex;
    setCurrentInputIndex(currentOutputIndex);
    setCurrentOutputIndex(tempIndex);
  };
  
  if (!currentInput || !currentOutput) {
    return (
      <div className="image-comparer-empty">
        <Empty description="Select images to compare" />
      </div>
    );
  }
  
  return (
    <div className="image-comparer">
      <div className="comparer-controls">
        <Space size={16} wrap>
          <div className="control-group">
            <span className="control-label">Before:</span>
            <Select
              value={currentInputIndex}
              onChange={setCurrentInputIndex}
              style={{ width: 200 }}
              options={inputImages.map((img, idx) => ({
                label: img.label,
                value: idx
              }))}
            />
          </div>
          
          <Button
            icon={<SwapOutlined />}
            onClick={handleSwap}
            className="swap-btn"
          >
            Swap
          </Button>
          
          <div className="control-group">
            <span className="control-label">After:</span>
            <Select
              value={currentOutputIndex}
              onChange={setCurrentOutputIndex}
              style={{ width: 200 }}
              options={outputImages.map((img, idx) => ({
                label: img.label,
                value: idx
              }))}
            />
          </div>
          
          <div className="control-group">
            <span className="control-label">Position:</span>
            <Slider
              value={sliderPosition}
              onChange={setSliderPosition}
              style={{ width: 150 }}
            />
          </div>
          
          <Button
            icon={<FullscreenOutlined />}
            onClick={() => onImageClick(currentOutput)}
          >
            Fullscreen
          </Button>
        </Space>
      </div>
      
      <div 
        className="comparer-container"
        ref={comparerRef}
        onMouseDown={() => setIsDragging(true)}
      >
        <div className="comparer-image-wrapper">
          {/* Before image - always visible */}
          <img 
            src={currentInput.url} 
            alt="Before" 
            className="comparer-image comparer-before-img"
          />
          
          {/* After image - masked by slider */}
          <img 
            src={currentOutput.url} 
            alt="After" 
            className="comparer-image comparer-after-img"
            style={{
              clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
            }}
          />
          
          <div 
            className="comparer-slider"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="slider-line" />
            <div className="slider-handle">
              <SwapOutlined />
            </div>
          </div>
        </div>
        
        <div className="comparer-labels">
          <div className="label-before">Before</div>
          <div className="label-after">After</div>
        </div>
      </div>
    </div>
  );
};

export default ImageComparer;
